import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useApiKeys } from "@/hooks/useApiKeys";
import { Key, Plus, Trash2, Eye, EyeOff, Loader2, Lock, Info } from "lucide-react";

interface ApiKeyManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string | null;
}

export function ApiKeyManager({ open, onOpenChange, projectId }: ApiKeyManagerProps) {
  const { apiKeys, loading, saving, fetchApiKeys, addApiKey, deleteApiKey } = useApiKeys(projectId);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [showValue, setShowValue] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (open && projectId) {
      fetchApiKeys();
    }
  }, [open, projectId, fetchApiKeys]);

  const validateName = (name: string): boolean => {
    if (!name.trim()) {
      setNameError(null);
      return false;
    }
    const normalized = name.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    if (!/^[A-Z][A-Z0-9_]*$/.test(normalized)) {
      setNameError("Must be uppercase, start with a letter (e.g. OPENAI_API_KEY)");
      return false;
    }
    setNameError(null);
    return true;
  };

  const handleAdd = async () => {
    const name = newName.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    if (!name || !newValue.trim()) return;
    if (!validateName(name)) return;
    const success = await addApiKey(name, newValue);
    if (success) {
      setNewName("");
      setNewValue("");
      setNameError(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            API Keys
          </DialogTitle>
          <DialogDescription>
            These keys are available in your app as{" "}
            <code className="bg-muted px-1 rounded text-xs">import.meta.env.VITE_KEY_NAME</code>
          </DialogDescription>
        </DialogHeader>

        {/* Security note */}
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 flex gap-3">
          <Lock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            Values are encrypted at rest and injected as environment variables when you deploy.
          </p>
        </div>

        {/* Add key form */}
        <div className="space-y-3 border-b border-border pb-4">
          <Label className="text-sm font-medium">Add New Key</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="OPENAI_API_KEY"
                value={newName}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
                  setNewName(v);
                  if (v) validateName(v);
                  else setNameError(null);
                }}
                className={nameError ? "font-mono text-sm border-destructive" : "font-mono text-sm"}
              />
              {nameError && <p className="text-xs text-destructive mt-1">{nameError}</p>}
            </div>
            <div className="flex-1 relative">
              <Input
                type={showValue ? "text" : "password"}
                placeholder="sk-..."
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="font-mono text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowValue(!showValue)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              onClick={handleAdd}
              disabled={!newName.trim() || !newValue.trim() || !!nameError || saving}
              size="icon"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Keys list */}
        <ScrollArea className="flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Info className="h-8 w-8 text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No API keys yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Add keys like OPENAI_API_KEY, STRIPE_KEY, etc.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-border bg-muted/30"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm font-medium truncate">{key.key_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{key.masked_value}</p>
                    {key.project_id === null && (
                      <span className="text-xs text-primary">Global</span>
                    )}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive flex-shrink-0">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete API Key</AlertDialogTitle>
                        <AlertDialogDescription>
                          Delete <code className="bg-muted px-1 rounded">{key.key_name}</code>? This may break your app if it depends on this key.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteApiKey(key.key_name)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
